import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Flame, Beef, Plus, Trash2, Utensils, Target, BookmarkPlus, Zap, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useSiteTime } from "../lib/time";
import MealGroupedEntries from "./nutrition-log/MealGroupedEntries";
import GoalTile from "./nutrition-log/GoalTile";
import AIMealParser from "./nutrition-log/AIMealParser";
import { localDayISO } from "./nutrition-log/utils";


export default function NutritionLogCard({ onEntryChange } = {}) {
  const { user, refreshUser } = useAuth();
  const { tz } = useSiteTime();
  const { settings } = useSettings();
  const aiEnabled =
    (settings?.feature_flags?.ai_features ?? true) !== false &&
    (settings?.feature_flags?.ai_nutrition ?? true) !== false;
  const [today, setToday] = useState({ day: localDayISO(), totals: { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sugar_g: 0, fiber_g: 0, sodium_mg: 0, count: 0 }, entries: [] });
  const [calories, setCalories] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [sugar, setSugar] = useState("");
  const [fiber, setFiber] = useState("");
  const [sodium, setSodium] = useState("");
  const [mealGroup, setMealGroup] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editCalGoal, setEditCalGoal] = useState(false);
  const [editProtGoal, setEditProtGoal] = useState(false);
  const [calGoalDraft, setCalGoalDraft] = useState("");
  const [protGoalDraft, setProtGoalDraft] = useState("");

  // Meal presets — one-tap add for common meals
  const [presets, setPresets] = useState([]);
  const [savePresetName, setSavePresetName] = useState("");
  const [showSavePreset, setShowSavePreset] = useState(false);

  const loadPresets = useCallback(async () => {
    try {
      const { data } = await api.get("/meal-presets");
      setPresets(data.items || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const addPreset = async (p) => {
    try {
      await api.post("/nutrition", {
        calories: p.calories,
        protein_g: p.protein_g,
        meal_group: mealGroup || null,
        note: p.name,
        day: localDayISO(),
      });
      await load();
      onEntryChange?.();
      toast.success(`Added ${p.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const savePreset = async () => {
    const c = parseFloat(calories);
    const p = parseFloat(protein);
    const nm = savePresetName.trim();
    if (!nm) { toast.error("Name the preset first"); return; }
    if (Number.isNaN(c) && Number.isNaN(p)) { toast.error("Enter calories or protein"); return; }
    try {
      await api.post("/meal-presets", {
        name: nm,
        calories: Number.isNaN(c) ? 0 : c,
        protein_g: Number.isNaN(p) ? 0 : p,
      });
      setSavePresetName("");
      setShowSavePreset(false);
      await loadPresets();
      toast.success(`Saved preset "${nm}"`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/meal-presets/${id}`);
      setPresets((prev) => prev.filter((p) => p.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };


  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/nutrition/today", { params: { day: localDayISO() } });
      setToday(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAdd = async (e) => {
    e.preventDefault();
    const c = parseFloat(calories);
    const p = parseFloat(protein);
    const cb = parseFloat(carbs);
    const ft = parseFloat(fat);
    const sg = parseFloat(sugar);
    const fb = parseFloat(fiber);
    const sd = parseFloat(sodium);
    const anyEntered = [c, p, cb, ft, sg, fb, sd].some((v) => !Number.isNaN(v));
    if (!anyEntered) {
      toast.error("Enter at least one macro value");
      return;
    }
    setSaving(true);
    try {
      await api.post("/nutrition", {
        calories: Number.isNaN(c) ? 0 : c,
        protein_g: Number.isNaN(p) ? 0 : p,
        carbs_g: Number.isNaN(cb) ? 0 : cb,
        fat_g: Number.isNaN(ft) ? 0 : ft,
        sugar_g: Number.isNaN(sg) ? 0 : sg,
        fiber_g: Number.isNaN(fb) ? 0 : fb,
        sodium_mg: Number.isNaN(sd) ? 0 : sd,
        meal_group: mealGroup || null,
        note: note.trim() || null,
        day: localDayISO(),
      });
      setCalories(""); setProtein(""); setCarbs(""); setFat("");
      setSugar(""); setFiber(""); setSodium("");
      setNote("");
      // Keep mealGroup selected — user is likely logging multiple items into the same meal
      await load();
      onEntryChange?.();
      toast.success(`Logged${mealGroup ? ` to ${mealGroup}` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    // Snapshot the entry before delete so we can restore it if the user hits Undo.
    const entry = today.entries.find((e) => e.id === id);
    try {
      await api.delete(`/nutrition/${id}`);
      await load();
      onEntryChange?.();
      if (entry) {
        const label = (entry.note && entry.note.trim()) || `${Math.round(entry.calories || 0)} kcal entry`;
        toast(`Deleted: ${label}`, {
          duration: 10000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.post("/nutrition", {
                  calories: entry.calories || 0,
                  protein_g: entry.protein_g || 0,
                  carbs_g: entry.carbs_g || 0,
                  fat_g: entry.fat_g || 0,
                  sugar_g: entry.sugar_g || 0,
                  fiber_g: entry.fiber_g || 0,
                  sodium_mg: entry.sodium_mg || 0,
                  meal_group: entry.meal_group || null,
                  source: entry.source || "manual",
                  note: entry.note || null,
                  day: entry.day || localDayISO(),
                });
                await load();
                onEntryChange?.();
                toast.success("Restored");
              } catch (err) { toast.error(formatApiError(err)); }
            },
          },
        });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveGoal = async (field, draft) => {
    const parsed = draft === "" ? null : parseFloat(draft);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      toast.error("Enter a positive number");
      return;
    }
    try {
      await api.patch("/users/me", { [field]: parsed });
      await refreshUser?.();
      toast.success(parsed == null ? "Goal cleared" : "Goal saved");
      if (field === "calorie_goal") setEditCalGoal(false);
      if (field === "protein_goal_g") setEditProtGoal(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="nutrition-log-card">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488]">// Fuel</div>
          <div className="font-display font-black uppercase tracking-tight text-2xl leading-none mt-1 flex items-center gap-2">
            <Utensils className="w-5 h-5 text-[#0D9488]" /> Nutrition Log
          </div>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
          {today.totals.count} entr{today.totals.count === 1 ? "y" : "ies"} today
        </div>
      </div>

      {/* Totals with goal progress */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <GoalTile
          testid="nutrition-totals-calories"
          Icon={Flame}
          label="Calories"
          unitLabel="kcal today"
          unitShort="kcal"
          value={today.totals.calories}
          goal={user?.calorie_goal}
          editing={editCalGoal}
          setEditing={setEditCalGoal}
          goalDraft={calGoalDraft}
          setGoalDraft={setCalGoalDraft}
          onSaveGoal={() => saveGoal("calorie_goal", calGoalDraft)}
          onClearGoal={() => saveGoal("calorie_goal", "")}
          celebrateKey="cal"
          dayISO={today.day}
        />
        <GoalTile
          testid="nutrition-totals-protein"
          Icon={Beef}
          label="Protein"
          unitLabel="grams today"
          unitShort="g"
          value={today.totals.protein_g}
          goal={user?.protein_goal_g}
          editing={editProtGoal}
          setEditing={setEditProtGoal}
          goalDraft={protGoalDraft}
          setGoalDraft={setProtGoalDraft}
          onSaveGoal={() => saveGoal("protein_goal_g", protGoalDraft)}
          onClearGoal={() => saveGoal("protein_goal_g", "")}
          celebrateKey="prot"
          dayISO={today.day}
        />
      </div>

      <AIMealParser
        mealGroup={mealGroup}
        aiEnabled={aiEnabled}
        onLogged={async () => { await load(); onEntryChange?.(); }}
      />

      {/* Meal presets — one-tap add */}
      <div className="mb-4" data-testid="meal-presets-section">
        <div className="flex items-baseline justify-between mb-2">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-[#0D9488]" /> // One-tap presets
          </div>
          <button
            type="button"
            onClick={() => setShowSavePreset((v) => !v)}
            className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0D9488] transition-colors inline-flex items-center gap-1"
            data-testid="meal-preset-save-toggle"
          >
            <BookmarkPlus className="w-3 h-3" /> {showSavePreset ? "Cancel" : "Save current as preset"}
          </button>
        </div>
        {showSavePreset && (
          <div className="flex gap-2 mb-2">
            <Input
              value={savePresetName}
              onChange={(e) => setSavePresetName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") savePreset(); }}
              placeholder="e.g. Chicken + rice bowl"
              maxLength={80}
              className="bg-slate-50 border-slate-200 rounded-none h-9 text-sm"
              data-testid="meal-preset-name-input"
            />
            <Button
              type="button"
              onClick={savePreset}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-9 px-3 uppercase tracking-widest text-[10px] font-mono-num font-semibold shrink-0"
              data-testid="meal-preset-save-btn"
            >
              Save
            </Button>
          </div>
        )}
        {presets.length === 0 ? (
          <div className="border border-dashed border-slate-200 py-3 px-3 text-center text-slate-400 text-[11px] font-mono-num uppercase tracking-widest">
            Save your first meal above — then tap to log it in a second.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <div
                key={p.id}
                className="group inline-flex items-center border border-slate-200 bg-slate-50 hover:border-[#0D9488] transition-colors"
                data-testid={`meal-preset-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => addPreset(p)}
                  className="px-3 py-1.5 text-left flex items-center gap-1.5"
                  data-testid={`meal-preset-add-${p.id}`}
                >
                  <Plus className="w-3 h-3 text-[#0D9488]" />
                  <span className="text-sm text-slate-900 dark:text-white group-hover:text-[#0D9488]">{p.name}</span>
                  <span className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
                    {Math.round(p.calories)}kcal · {Math.round(p.protein_g)}g P
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => deletePreset(p.id)}
                  className="w-7 h-full flex items-center justify-center border-l border-slate-200 text-slate-400 hover:text-[#FF3B30]"
                  aria-label="Delete preset"
                  data-testid={`meal-preset-delete-${p.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add form — 7 macros + meal group */}
      <form onSubmit={onAdd} className="space-y-3" data-testid="nutrition-add-form">
        {/* Meal group selector — pill row */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num mb-1.5 block">Meal group (optional)</Label>
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: "breakfast", label: "Breakfast", emoji: "🍳" },
              { key: "lunch",     label: "Lunch",     emoji: "🥗" },
              { key: "dinner",    label: "Dinner",    emoji: "🍲" },
              { key: "snack",     label: "Snack",     emoji: "🍎" },
            ].map((mg) => {
              const active = mealGroup === mg.key;
              return (
                <button
                  key={mg.key}
                  type="button"
                  onClick={() => setMealGroup(active ? "" : mg.key)}
                  className={[
                    "inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-widest transition-colors",
                    active
                      ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                      : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:hover:text-white",
                  ].join(" ")}
                  data-testid={`meal-group-${mg.key}`}
                >
                  <span aria-hidden>{mg.emoji}</span> {mg.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 7-macro grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Calories</Label>
            <Input
              type="number" step="1" min="0" inputMode="numeric"
              value={calories} onChange={(e) => setCalories(e.target.value)}
              placeholder="520"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-calories-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-rose-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" />Protein (g)</Label>
            <Input
              type="number" step="0.1" min="0" inputMode="decimal"
              value={protein} onChange={(e) => setProtein(e.target.value)}
              placeholder="45"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-protein-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-blue-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" />Carbs (g)</Label>
            <Input
              type="number" step="0.1" min="0" inputMode="decimal"
              value={carbs} onChange={(e) => setCarbs(e.target.value)}
              placeholder="60"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-carbs-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-orange-400" />Fat (g)</Label>
            <Input
              type="number" step="0.1" min="0" inputMode="decimal"
              value={fat} onChange={(e) => setFat(e.target.value)}
              placeholder="18"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-fat-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-pink-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-pink-400" />Sugar (g)</Label>
            <Input
              type="number" step="0.1" min="0" inputMode="decimal"
              value={sugar} onChange={(e) => setSugar(e.target.value)}
              placeholder="8"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-sugar-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-violet-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-400" />Fiber (g)</Label>
            <Input
              type="number" step="0.1" min="0" inputMode="decimal"
              value={fiber} onChange={(e) => setFiber(e.target.value)}
              placeholder="5"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-fiber-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-mono-num flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Sodium (mg)</Label>
            <Input
              type="number" step="1" min="0" inputMode="numeric"
              value={sodium} onChange={(e) => setSodium(e.target.value)}
              placeholder="450"
              className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num"
              data-testid="nutrition-sodium-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Note</Label>
            <Input
              type="text" maxLength={200}
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Chicken bowl…"
              className="bg-slate-50 border-slate-200 rounded-none h-10"
              data-testid="nutrition-note-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="w-full h-11 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-xs font-semibold"
          data-testid="nutrition-add-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {saving ? "Adding…" : `Log ${mealGroup ? `to ${mealGroup}` : "entry"}`}
        </Button>
      </form>

      {/* Today's entries — grouped by meal */}
      {today.entries.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-200" data-testid="nutrition-entries-list">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">// Today's meals</div>
          <MealGroupedEntries entries={today.entries} tz={tz} onDelete={onDelete} />
        </div>
      )}

      {loading && today.entries.length === 0 && (
        <div className="mt-4 text-center text-slate-400 text-xs font-mono-num uppercase tracking-widest">Loading…</div>
      )}
    </Card>
  );
}
